import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ReactNode, useEffect, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { useHasAccess } from "../hooks/useHasAccess";
import { type MoveTarget, useMoveTargets } from "../hooks/useMoveTargets";
import { useMoveWorkflow } from "../hooks/useMoveWorkflow";
import { spaceIcon } from "../lib/spaceIcon";
import { makeUrl, navigate, routes } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { InfoIcon } from "./InfoIcon";
import { MoveWorkflowModal } from "./MoveWorkflowModal";
import { Text } from "./Text";
import { Toggle } from "./Toggle";
import { Tooltip } from "./Tooltip";
import { WorkflowSettings } from "./WorkflowSettings";

const AutofixLabel = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

type Props = {
  workflowId: string;
  spaceId: string;
  isFavorite: boolean;
  autofixEnabled: boolean;
  autoImproveEnabled: boolean;
  opener?: ReactNode;
  afterDelete?: () => void;
};

function MoveTargetsLoader({
  onTargets,
}: {
  onTargets: (targets: MoveTarget[]) => void;
}) {
  const targets = useMoveTargets();
  useEffect(() => {
    onTargets(targets);
  }, [targets, onTargets]);
  return null;
}

export function WorkflowActionsMenu({
  workflowId,
  spaceId,
  isFavorite,
  autofixEnabled,
  autoImproveEnabled,
  opener,
  afterDelete,
}: Props) {
  const z = useZero();
  const canWrite = useHasAccess("space:write", spaceId);
  const canCreateSpace = useHasAccess("tenant:space_create");
  const {
    requestMove,
    pending: movePending,
    confirmMove,
    cancelMove,
  } = useMoveWorkflow();
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveTargets, setMoveTargets] = useState<MoveTarget[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [retentionOpen, setRetentionOpen] = useState(false);

  const workflowUrl = makeUrl(routes.space.workflow, { spaceId, workflowId });

  const availableTargets = moveTargets.filter((s) => s.id !== spaceId);
  const createSpaceItem = canCreateSpace
    ? [
        {
          type: "button" as const,
          left: <Icon name="plus" size={16} />,
          label: "Create space",
          onClick: () => {
            document.dispatchEvent(
              new CustomEvent("create-space", { detail: workflowId })
            );
          },
        },
      ]
    : [];
  const moveSubmenuItem = ({ disabled }: { disabled?: boolean }) => ({
    type: "submenu" as const,
    left: <Icon name="input" size={16} />,
    label: "Move",
    disabled,
    submenu: [
      {
        items:
          availableTargets.length > 0
            ? availableTargets.map((s) => ({
                type: "button" as const,
                key: s.id,
                disabled: s.disabled,
                left: <Icon name={spaceIcon(s.mode)} size={16} />,
                label: s.disabledReason ? (
                  <Tooltip content={s.disabledReason}>
                    <span>{s.name}</span>
                  </Tooltip>
                ) : (
                  s.name
                ),
                onClick: () =>
                  requestMove({
                    workflowId,
                    targetSpaceId: s.id,
                    targetSpaceName: s.name,
                  }),
              }))
            : createSpaceItem,
      },
    ],
  });

  const autofixLabelNode = (
    <AutofixLabel>
      <span>Autofix</span>
      <Tooltip
        content={
          <div style={{ width: 200 }}>
            <Text variant="text-small-md">
              When enabled, 3B monitors for errors and automatically creates a
              fix branch.
            </Text>
          </div>
        }
      >
        <InfoIcon>
          <Icon name="info" size={12} />
        </InfoIcon>
      </Tooltip>
    </AutofixLabel>
  );

  const autoImproveLabelNode = (
    <AutofixLabel>
      <span>Autotune</span>
      <Tooltip
        content={
          <div style={{ width: 200 }}>
            <Text variant="text-small-md">
              When enabled, 3B continuously looks for ways to make this workflow
              more reliable, faster, or more capable, and proposes them on a
              branch.
            </Text>
          </div>
        }
      >
        <InfoIcon>
          <Icon name="info" size={12} />
        </InfoIcon>
      </Tooltip>
    </AutofixLabel>
  );

  const primaryItems = [
    {
      type: "button" as const,
      left: <Icon name="link-2" size={16} />,
      label: "Copy link",
      onClick: () => {
        void writeToClipboard(window.location.origin + workflowUrl);
      },
    },
    {
      type: "button" as const,
      left: <Icon name="box" size={16} />,
      label: "Browse storage",
      onClick: () => navigate(routes.modal.storage, { spaceId, workflowId }),
    },
    {
      type: "button" as const,
      left: <Icon name={isFavorite ? "heart-crack" : "heart"} size={16} />,
      label: isFavorite ? "Remove favorite" : "Favorite",
      onClick: () => {
        z.mutate(sharedMutators.toggleWorkflowFavorite({ workflowId }));
      },
    },
    {
      type: "button" as const,
      keepOpen: true,
      left: <Icon name="radar" size={16} />,
      label: autofixLabelNode as unknown as string,
      disabled: !canWrite,
      right: (
        <Toggle
          isOn={autofixEnabled}
          disabled={!canWrite}
          onToggle={() => {
            z.mutate(sharedMutators.toggleAutofix({ workflowId }));
          }}
        />
      ),
      onClick: () => {
        z.mutate(sharedMutators.toggleAutofix({ workflowId }));
      },
    },
    {
      type: "button" as const,
      keepOpen: true,
      left: <Icon name="audio-waveform" size={16} />,
      label: autoImproveLabelNode as unknown as string,
      disabled: !canWrite,
      right: (
        <Toggle
          isOn={autoImproveEnabled}
          disabled={!canWrite}
          onToggle={() => {
            z.mutate(sharedMutators.toggleAutoImprove({ workflowId }));
          }}
        />
      ),
      onClick: () => {
        z.mutate(sharedMutators.toggleAutoImprove({ workflowId }));
      },
    },
    {
      type: "button" as const,
      left: <Icon name="calendar" size={16} />,
      label: "Execution retention",
      disabled: !canWrite,
      onClick: () => setRetentionOpen(true),
    },
    moveSubmenuItem({ disabled: !canWrite }),
  ];

  return (
    <>
      {menuOpen && <MoveTargetsLoader onTargets={setMoveTargets} />}
      <Dropdown
        opener={opener ?? <Button variant="secondary" icon="more" />}
        minWidth={160}
        onOpenChange={setMenuOpen}
        sections={[
          { items: primaryItems },
          {
            items: [
              {
                type: "button" as const,
                left: <Icon name="trash" size={16} />,
                label: "Delete",
                disabled: !canWrite,
                onClick: () => setConfirmDeleteOpen(true),
              },
            ],
          },
        ]}
      />
      {movePending && (
        <MoveWorkflowModal
          workflowId={movePending.workflowId}
          targetSpaceId={movePending.targetSpaceId}
          targetSpaceName={movePending.targetSpaceName}
          onClose={cancelMove}
          onConfirm={confirmMove}
        />
      )}
      {retentionOpen && (
        <WorkflowSettings
          workflowId={workflowId}
          onClose={() => setRetentionOpen(false)}
        />
      )}
      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          z.mutate(sharedMutators.deleteWorkflow({ workflowId }));
          setConfirmDeleteOpen(false);
          afterDelete?.();
        }}
        title="Delete workflow"
        description="This workflow and all its steps will be deleted. You can recover it from the space menu for 30 days."
        confirmLabel="Delete workflow"
        destructive
      />
    </>
  );
}
